"use client"

import { ThemeToggle } from "@/components/theme-toggle"
import { cn } from "@/lib/utils"

/**
 * Theme switcher for signed-OUT screens (welcome / login / signup / forced
 * password change).
 *
 * Every authed shell already renders a ThemeToggle in its top-right chrome —
 * user-layout (trader frame overlay), admin-layout and superadmin-layout
 * (header bar). Public pages render outside those shells, so before this
 * component a visitor could not change the theme until after logging in:
 * whatever `system` resolved to was the only option on the very screens a
 * first-time user sees. Same underlying component as the authed shells, so
 * the choice is the same next-themes value and survives the login.
 *
 * Two placements, both landing on the top-right so the button does not jump
 * when the user signs in:
 *  - "frame" — absolute, for the trader phone frame. Uses the same
 *    coordinates as the user-layout overlay cluster (right-3 / top-3 plus
 *    the safe-area inset). `absolute` resolves against the frame's PADDING
 *    box, so the frame's `padding-top: env(safe-area-inset-top)` is not
 *    inherited by `top` — it has to be added back explicitly, exactly as
 *    user-layout does.
 *  - "page" — fixed to the viewport corner, matching where the admin and
 *    superadmin headers put their toggle.
 *
 * The wrapper is pointer-events-none so an overlay can never swallow clicks
 * on the content beneath it; the button itself opts back in.
 */
export function PublicThemeToggle({
  variant = "frame",
  className,
}: {
  variant?: "frame" | "page"
  className?: string
}) {
  return (
    <div
      className={cn(
        "pointer-events-none z-50 flex items-center",
        variant === "frame" ? "absolute right-3" : "fixed right-4",
        className
      )}
      style={{
        top:
          variant === "frame"
            ? "calc(env(safe-area-inset-top, 0px) + 0.75rem)"
            : "calc(env(safe-area-inset-top, 0px) + 1rem)",
      }}
    >
      <div className="pointer-events-auto">
        <ThemeToggle />
      </div>
    </div>
  )
}
